import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
    static values = { ackUrl: String, guest: Boolean, pollUrl: String }

    connect() {
        this.connected = true
        this.abortController = new AbortController()
        this._schedulePoll()
    }

    disconnect() {
        this.connected = false
        clearTimeout(this.pollTimer)
        this.abortController.abort()
    }

    _schedulePoll() {
        if (!this.connected || !this.pollUrlValue) return
        this.pollTimer = setTimeout(() => this._poll(), 4000)
    }

    async _poll() {
        if (document.hidden) return this._schedulePoll()
        try {
            const response = await fetch(this.pollUrlValue, {
                headers: { Accept: "application/json" },
                signal: this.abortController.signal
            })
            if (!response.ok) throw new Error("Panel unavailable")
            const payload = await response.json()
            if (this.connected && payload.panel) Turbo.renderStreamMessage(payload.panel)
        } catch (_error) {
            this._schedulePoll()
        }
    }

    completed() {
        // Acknowledge without replacing the panel while the editor opens.
        // Keep this request alive across navigation; disconnect aborts polling.
        if (!this.hasAckUrlValue) return
        fetch(this.ackUrlValue, {
            method: "POST",
            keepalive: true,
            headers: {
                "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
                Accept: "application/json"
            }
        }).catch(() => {})
    }

    async createAnother(event) {
        // Keep finished designs available through refreshes and cancelled login.
        const response = await fetch(event.currentTarget.dataset.aiPanelUrl, {
            headers: { Accept: "application/json" },
            signal: this.abortController.signal
        }).catch(() => null)
        if (!response?.ok || !this.connected) return
        const payload = await response.json()
        if (this.connected && payload.panel) this._openPrompt(payload.panel)
    }

    _openPrompt(panel) {
        const url = new URL(window.location.href)
        url.searchParams.set("ai", "1")
        window.history.replaceState(window.history.state, "", url)
        Turbo.renderStreamMessage(panel)
    }

    async dismiss() {
        if (!this.hasAckUrlValue || this.dismissing) return
        this.dismissing = true
        try {
            const response = await fetch(this.ackUrlValue, {
                method: "POST",
                headers: {
                    "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
                    Accept: this.guestValue ? "application/json" : "text/vnd.turbo-stream.html"
                },
                signal: this.abortController.signal
            })
            if (response.ok && this.guestValue) {
                const payload = await response.json()
                if (this.connected && payload.panel) {
                    this._openPrompt(payload.panel)
                }
            }
        } catch (_error) { /* The existing result remains usable on failure. */ }
        finally { this.dismissing = false }
    }
}
